import { createContext, useContext, useState, useCallback, useRef } from "react"
import { extractId } from "../utils/transformers.js";
import { getEndUserDiningTotals, getEndUserDiningCommits } from "../api/index.js";
import { createStaleResponseGuard } from "../utils/staleResponseGuard.js";

/** The rows/History cache is keyed by date range AND filters (search/settled/refundState) —
 * every axis that changes what the server returns needs its own cache slot, or a stale answer
 * from one filter combination would be served under another. */
const diningFilterKey = ({ startDate, endDate, search, settled, refundState } = {}) =>
    `${startDate || ''}|${endDate || ''}|${search || ''}|${settled === undefined ? '' : settled}|${refundState || ''}`

/** The totals endpoint (nimi-backend#1085) takes only the date window and the member — it is a
 * period summary, not filtered by search/settled/refundState — so its cache key is date range
 * only. Keying it by the row filters too would silently refetch identical totals on every
 * keystroke in the search box. */
const diningTotalsKey = ({ startDate, endDate } = {}) =>
    `${startDate || ''}|${endDate || ''}`

export const EndUsersContext = createContext(null)


export function EndUsersProvider({ children }) {

    const [endUsersMap, setEndUsersMap] = useState(null)
    const [segmentsMap, setSegmentsMap] = useState(null)
    const [paginationData, setPaginationData] = useState({})
    const [eventsData, setEventsData] = useState(null)
    const [seasonTicketsData, setSeasonTicketsData] = useState(null)
    const [activityData, setActivityData] = useState(null)
    const [endUsersFullDataMap, setEndUsersFullDataMap] = useState({})
    const [allAccountsData, setAllAccountsData] = useState(null)
    /* { [endUserId]: { [filterKey]: { totals, error } } } — the Overview cards + donut. */
    const [endUserDiningTotalsMap, setEndUserDiningTotalsMap] = useState({})
    /* { [endUserId]: { [filterKey]: { [page]: { commits, pagination, error } } } } — the History list. */
    const [endUserDiningCommitsMap, setEndUserDiningCommitsMap] = useState({})
    // Keyed per endUserId, shared by both dining caches above. Lets a fetch already in flight when
    // invalidateEndUserDiningData runs recognise its own eventual response as stale and drop the
    // write, instead of repopulating a cache slot it was just invalidated out of. A ref, not state:
    // it only guards a write from inside an async callback and must never itself trigger a
    // re-render. See staleResponseGuard.js for the guarded race and its unit test.
    const diningResponseGuardRef = useRef(createStaleResponseGuard())

    /* Map relations, an object which maps higher entity to it relations - { idOfTheEntity: relatedEntitiesId[] }  */
    const [sellerAccountToEndUsersMap, setSellerAccountToEndUsersMap] = useState({})
    const [sellerAccountToSegmentMap, setSellerAccountToSegmentMap] = useState({})
    const [segmentToEndUsersMap, setSegmentToEndUsersMap] = useState({})
    const [reload, setReload] = useState(0)

    /* End Users */
    const updateManyEndUsers = (endUsers) => {
        const newEndUsersMap = JSON.parse(JSON.stringify(endUsersMap ? endUsersMap : {}))
        endUsers.forEach(endUser => { newEndUsersMap[endUser._id] = endUser })
        setEndUsersMap(newEndUsersMap)
    }
    const updateSellerAccountToEndUsersRelation = (sellerAccountId, endUsers) => {
        const ids = endUsers.map(extractId)
        const newSellerAccountToEndUsersMap = JSON.parse(JSON.stringify(sellerAccountToEndUsersMap))
        const newArray = newSellerAccountToEndUsersMap[sellerAccountId]?.length ? [...newSellerAccountToEndUsersMap[sellerAccountId], ...ids] : ids
        newSellerAccountToEndUsersMap[sellerAccountId] = [...new Set(newArray)] // Force to remove duplications
        setSellerAccountToEndUsersMap(newSellerAccountToEndUsersMap)
    }
    const getEndUser = (id) => endUsersMap[id]
    const getEndUsersOfSellerAccount = (sellerAccountId) => sellerAccountToEndUsersMap[sellerAccountId]

    const updateEndUsersFullData = (endUserId, accounts, events, userEvents, endUser, eventOrders, endUserSeasonTickets, crmEvents) => {
        const newMapData = JSON.parse(JSON.stringify(endUsersFullDataMap))
        newMapData[endUserId] = { ...newMapData[endUserId] }

        if (accounts) {
            newMapData[endUserId].accounts = accounts
        }

        if (events) {
            newMapData[endUserId].events = events
        }

        if (userEvents) {
            newMapData[endUserId].userEvents = userEvents
        }

        if (endUser) {
            newMapData[endUserId].endUser = endUser
        }

        if (eventOrders) {
            newMapData[endUserId].eventOrders = eventOrders
        }

        if (endUserSeasonTickets) {
            newMapData[endUserId].endUserSeasonTickets = endUserSeasonTickets
        }

        if (crmEvents) {
            newMapData[endUserId].crmEvents = crmEvents
        }

        // newMapData[endUserId] = { accounts, events, userEvents, endUser, eventOrders, endUserSeasonTickets, crmEvents }
        setEndUsersFullDataMap(newMapData)
    }
    const updateEndUserData = (endUserId, endUserData) => {
        const newMapData = JSON.parse(JSON.stringify(endUsersFullDataMap))
        newMapData[endUserId].endUser = endUserData
        setEndUsersFullDataMap(newMapData)
    }
    const getEndUserFullData = (endUserId) => endUsersFullDataMap[endUserId]

    /* Dining (POS commits) — Overview totals. Takes only startDate/endDate (+ endUserId as the
     * outer map key) — search/settled/refundState are the History list's own filters and do not
     * narrow this endpoint (nimi-backend#1085): the cards are a period summary, not a total of
     * whatever the list is currently filtered to. Callers may still pass those three through
     * (harmless — they're simply ignored here), so this can share a call-site shape with
     * fetchEndUserDiningCommits, but they never reach the request or the cache key. */
    const getEndUserDiningTotalsData = (endUserId, filters) =>
        endUserDiningTotalsMap[endUserId]?.[diningTotalsKey(filters)]

    // Cached per customer+date-range so revisiting the tab, or changing a row-only filter that
    // doesn't affect totals, doesn't refetch. A failure is cached too, as { error: true } —
    // otherwise a caller keyed on "no entry yet" would retry in a loop. Pass force to bypass the
    // cache after a mutation (see invalidateEndUserDiningData).
    const fetchEndUserDiningTotals = useCallback(async ({
        endUserId, marketplaceId, sellerId, startDate, endDate, force
    }) => {
        if (!endUserId || !marketplaceId || !sellerId) return null

        const key = diningTotalsKey({ startDate, endDate })
        if (!force) {
            const cached = endUserDiningTotalsMap[endUserId]?.[key]
            if (cached) return cached
        }

        // Snapshot the generation this request started against. An invalidation that lands while
        // it's in flight bumps it — if that happened, this response is answering a question that
        // no longer applies (a charge/refund/compensation moved the numbers), and writing it now
        // would resurrect the exact stale cache entry invalidation just cleared.
        const startedGeneration = diningResponseGuardRef.current.start(endUserId)

        let entry
        try {
            const { data } = await getEndUserDiningTotals({ marketplaceId, sellerId, endUserId, startDate, endDate })
            entry = { totals: data?.totals || null, error: false }
        } catch {
            entry = { totals: null, error: true }
        }

        if (!diningResponseGuardRef.current.isCurrent(endUserId, startedGeneration)) return entry

        setEndUserDiningTotalsMap(prev => ({
            ...prev,
            [endUserId]: { ...(prev[endUserId] || {}), [key]: entry }
        }))
        return entry
    }, [endUserDiningTotalsMap])

    /* Dining (POS commits) — History rows, one page at a time */
    const getEndUserDiningCommitsData = (endUserId, filters, page = 1) =>
        endUserDiningCommitsMap[endUserId]?.[diningFilterKey(filters)]?.[page]

    const fetchEndUserDiningCommits = useCallback(async ({
        endUserId, marketplaceId, sellerId, startDate, endDate, search, settled, refundState, page = 1, limit, force
    }) => {
        if (!endUserId || !marketplaceId || !sellerId) return null

        const filters = { startDate, endDate, search, settled, refundState }
        const key = diningFilterKey(filters)
        if (!force) {
            const cached = endUserDiningCommitsMap[endUserId]?.[key]?.[page]
            if (cached) return cached
        }

        const startedGeneration = diningResponseGuardRef.current.start(endUserId)

        let entry
        try {
            const { data } = await getEndUserDiningCommits({ marketplaceId, sellerId, endUserId, startDate, endDate, search, settled, refundState, page, limit })
            entry = { commits: data?.commits || [], pagination: data?.pagination || null, error: false }
        } catch {
            entry = { commits: [], pagination: null, error: true }
        }

        if (!diningResponseGuardRef.current.isCurrent(endUserId, startedGeneration)) return entry

        setEndUserDiningCommitsMap(prev => ({
            ...prev,
            [endUserId]: {
                ...(prev[endUserId] || {}),
                [key]: { ...(prev[endUserId]?.[key] || {}), [page]: entry }
            }
        }))
        return entry
    }, [endUserDiningCommitsMap])

    // A charge, settle or refund on the Billing tab changes what the Overview tab's cached totals
    // and History rows should say, but Billing talks to its own endpoints and never touches these
    // caches — so without this, switching tabs kept showing pre-charge/pre-refund numbers until a
    // full page reload. Clears EVERY cached filter combination and page for this member (not just
    // the active one): a charge can pull meals from a range/filter the admin isn't currently viewing.
    const invalidateEndUserDiningData = (endUserId) => {
        // Called unconditionally (not just when a cache entry exists): a fetch can be in flight
        // for a slot that hasn't written its cache entry yet, and that request must still be
        // recognised as stale when it resolves.
        diningResponseGuardRef.current.invalidate(endUserId)
        setEndUserDiningTotalsMap(prev => {
            if (!prev[endUserId]) return prev
            const next = { ...prev }
            delete next[endUserId]
            return next
        })
        setEndUserDiningCommitsMap(prev => {
            if (!prev[endUserId]) return prev
            const next = { ...prev }
            delete next[endUserId]
            return next
        })
    }

    // Drops a deleted end user from every local map/relation so the UI stays in sync without a reload.
    const removeEndUser = (id) => {
        setEndUsersMap(prev => {
            if (!prev || !prev[id]) return prev
            const next = { ...prev }
            delete next[id]
            return next
        })
        setEndUsersFullDataMap(prev => {
            if (!prev[id]) return prev
            const next = { ...prev }
            delete next[id]
            return next
        })
        setEndUserDiningTotalsMap(prev => {
            if (!prev[id]) return prev
            const next = { ...prev }
            delete next[id]
            return next
        })
        setEndUserDiningCommitsMap(prev => {
            if (!prev[id]) return prev
            const next = { ...prev }
            delete next[id]
            return next
        })
        setSellerAccountToEndUsersMap(prev => {
            let changed = false
            const next = {}
            for (const [accountId, ids] of Object.entries(prev)) {
                if (Array.isArray(ids) && ids.includes(id)) {
                    next[accountId] = ids.filter(existingId => existingId !== id)
                    changed = true
                } else {
                    next[accountId] = ids
                }
            }
            return changed ? next : prev
        })
    }

    /* Segments */
    const updateManySegments = (segments) => {
        const newSegmentsMap = JSON.parse(JSON.stringify(segmentsMap ? segmentsMap : {}))
        segments.forEach(segment => { newSegmentsMap[segment._id] = segment })
        setSegmentsMap(newSegmentsMap)
    }
    const updateSellerAccountToSegmentRelation = (sellerAccountId, segments) => {
        const ids = segments.length ? segments.map(extractId) : []
        const newSellerAccountToSegmentsMap = JSON.parse(JSON.stringify(sellerAccountToSegmentMap))
        const newArray = newSellerAccountToSegmentsMap[sellerAccountId]?.length ? [...newSellerAccountToSegmentsMap[sellerAccountId], ...ids] : ids
        newSellerAccountToSegmentsMap[sellerAccountId] = newArray // [ ...new Set(newArray)] // Force to remove duplications
        setSellerAccountToSegmentMap(newSellerAccountToSegmentsMap)
    }
    const getSegment = (id) => segmentsMap[id]
    const getSegmentsOfSellerAccount = (sellerAccountId) => sellerAccountToSegmentMap[sellerAccountId]
    // segment - should be array of objects like this { segmentId: string, endUsers: array of endUsers }
    const updateSegmentToEndUsersRelation = (segments, shouldRemove) => {
        const newSegmentToEndUsersMap = JSON.parse(JSON.stringify(segmentToEndUsersMap))
        for (const { segmentId, endUsers } of segments) {
            newSegmentToEndUsersMap[segmentId] = newSegmentToEndUsersMap[segmentId] ? newSegmentToEndUsersMap[segmentId] : {}
            for (const endUserRelation of endUsers) {
                newSegmentToEndUsersMap[segmentId][endUserRelation.endUser] = shouldRemove ? false : true
            }
        }
        setSegmentToEndUsersMap(newSegmentToEndUsersMap)
    }

    return (
        <EndUsersContext.Provider
            value={{
                updateManyEndUsers,
                getEndUser,
                updateSellerAccountToEndUsersRelation,
                getEndUsersOfSellerAccount,
                updateManySegments,
                updateSellerAccountToSegmentRelation,
                getSegment,
                getSegmentsOfSellerAccount,
                updateSegmentToEndUsersRelation,
                updateEndUsersFullData,
                getEndUserFullData,
                getEndUserDiningTotalsData,
                fetchEndUserDiningTotals,
                getEndUserDiningCommitsData,
                fetchEndUserDiningCommits,
                invalidateEndUserDiningData,
                updateEndUserData,
                removeEndUser,
                segmentsMap,
                allAccountsData,
                setAllAccountsData,
                segmentToEndUsersMap,
                paginationData,
                setPaginationData,
                eventsData,
                setEventsData,
                seasonTicketsData,
                setSeasonTicketsData,
                activityData,
                setActivityData,
                reload,
                setReload,
            }}>
            {children}
        </EndUsersContext.Provider>
    )
}

export const useEndUsersContext = () => {
    const context = useContext(EndUsersContext)
    if (!context) {
        throw new Error('useEndUsersContext context must be used inside EndUsersProvider')
    }
    return context
}
